import { Box, IconButton, Stack, Tooltip, useTheme } from '@mui/material';

import { VMP_SOLID_COLORS } from '@/constants/chartcolors.constants';
import { ASSET_RISK_SCORE_PARTS } from '@/pages/Assets/Assets.constants';
import type { CriticalityTypes } from '@/pages/VulnerabilityManagement/VulnerabilityManagement.types';
import getPonduranceScoreSeverity from '@/utils/getPonduranceScoreSeverity';

const MultiPartScoreBadge = ({
  score,
  parts,
  height,
  width,
  handleClick
}: {
  score: number;
  parts: { [key: string]: number };
  height?: number;
  width?: number;
  handleClick?: () => void;
}) => {
  const theme = useTheme();
  const severity = getPonduranceScoreSeverity(score);
  const fillColor =
    VMP_SOLID_COLORS[severity.toUpperCase() as CriticalityTypes] ||
    theme.vars.palette.grey[500];

  const values = Object.values(parts);

  const severities = values.map((value: number | null) =>
    value === null || !value
      ? null
      : (getPonduranceScoreSeverity(value).toUpperCase() as CriticalityTypes)
  );

  return (
    <Tooltip
      title={
        <Stack direction="column">
          {Object.entries(parts).map(([name, value], index) => (
            <Stack
              sx={{ alignItems: 'center', flexDirection: 'row' }}
              key={name}
            >
              <Box
                sx={{
                  marginRight: 1,
                  height: '8px',
                  width: '8px',
                  backgroundColor: severities[index]
                    ? VMP_SOLID_COLORS[severities[index]]
                    : theme.vars.palette.grey[500]
                }}
              />
              {
                ASSET_RISK_SCORE_PARTS[
                  name as keyof typeof ASSET_RISK_SCORE_PARTS
                ]
              }
              : {value ? getPonduranceScoreSeverity(value) : 'N/A'}
            </Stack>
          ))}
        </Stack>
      }
    >
      <IconButton
        disableRipple
        onClick={handleClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <svg
          width={width || 32}
          height={height || 32}
          viewBox="0 0 63.99 63.94"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="32" cy="31.94" r="21.68" fill={fillColor} />
          <path
            fill={
              values[0] && severities[0]
                ? VMP_SOLID_COLORS[severities[0]]
                : theme.vars.palette.grey[500]
            }
            d="M9.04,20.76c3.65-7.48,10.85-12.91,19.42-14.1.98-.14,1.71-.97,1.71-1.96V1.48c0-.98-.5-1.45-1.07-1.4C17.57,1.12,7.82,8.26,3.08,18.25c-.52,1.09.05,2.39,1.2,2.76l2.36.77c.94.31,1.96-.13,2.4-1.02Z"
          />
          <path
            fill={
              values[1] && severities[1]
                ? VMP_SOLID_COLORS[severities[1]]
                : theme.vars.palette.grey[500]
            }
            d="M14.27,50.32c-5.99-5.79-8.93-14.31-7.41-22.82.17-.97-.4-1.93-1.34-2.23l-3.06-.99c-.94-.3-1.53.02-1.66.59-2.57,11.28,1.21,22.76,9.24,30.35.88.83,2.29.69,3-.29l1.46-2.01c.58-.8.48-1.91-.23-2.6Z"
          />
          <path
            fill={
              values[2] && severities[2]
                ? VMP_SOLID_COLORS[severities[2]]
                : theme.vars.palette.grey[500]
            }
            d="M44,54.49c-7.35,3.91-16.37,4.07-24,0-.87-.47-1.95-.22-2.54.58l-1.89,2.6c-.58.8-.45,1.46.05,1.76,9.93,5.93,22.02,5.89,31.72.59,1.06-.58,1.36-1.96.65-2.94l-1.46-2.01c-.58-.8-1.66-1.05-2.54-.58Z"
          />
          <path
            fill={
              values[3] && severities[3]
                ? VMP_SOLID_COLORS[severities[3]]
                : theme.vars.palette.grey[500]
            }
            d="M57.15,27.5c1.44,8.2-1.19,16.83-7.42,22.82-.71.69-.81,1.79-.23,2.59l1.89,2.6c.58.8,1.25.88,1.69.5,8.71-7.61,12.4-19.12,10.36-29.99-.22-1.18-1.45-1.9-2.59-1.53l-2.36.77c-.94.31-1.51,1.26-1.34,2.24Z"
          />
          <path
            fill={
              values[4] && severities[4]
                ? VMP_SOLID_COLORS[severities[4]]
                : theme.vars.palette.grey[500]
            }
            d="M35.55,6.65c8.25,1.16,15.63,6.33,19.41,14.11.43.89,1.45,1.32,2.4,1.02l3.06-.99c.94-.3,1.23-.92,1-1.45C56.86,8.7,47.05,1.63,36.09.21c-1.2-.15-2.26.79-2.26,1.99v2.49c0,.99.73,1.83,1.71,1.97Z"
          />
          <text
            x="32"
            y="34"
            textAnchor="middle"
            dominantBaseline="middle"
            fill={
              severity === 'Critical' || severity === 'Low' ? 'white' : 'black'
            }
            fontSize="22"
            fontWeight="bold"
          >
            {Math.round(score)}
          </text>
        </svg>
      </IconButton>
    </Tooltip>
  );
};

export default MultiPartScoreBadge;
